'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import CrossIcon from '@images/small_icon/sign-up-close-icon.svg';
import { Language } from '@app/lib/store-context/Context';
import {
    SubmitSimpleSignupForm,
    submitSimpleSignupForm,
} from '@app/lib/actions/submit-simple-signup-form';
import {
    Button,
    Checkbox,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
    DialogTrigger,
    Input,
} from 'mmds';
import setIsDismissedCookieValue from '@app/components/ui/utils/set-is-dissmissed-cookie-value';
import getIsDismissedCookieValue from '@app/components/ui/utils/get-is-dissmissed-cookie-value';
import cn from 'clsx';
import s from './signup-modal.module.css';


const SignupModal = ({ lang }: { lang: Language }) => {
    const pathname = usePathname();
    const [pageViews, setPageViews] = useState<number>(1);
    const [currentPath, setCurrentPath] = useState<string>();
    const [open, setOpen] = useState<boolean>(false);
    const [pending, setPending] = useState<boolean>(false);
    const [formState, setFormState] = useState<SubmitSimpleSignupForm>({
        status: null,
        message: null,
    });

    useEffect(() => {
        const isDismissedCookieValue = getIsDismissedCookieValue('SignupModal');
        if (isDismissedCookieValue) {
            return;
        }

        if(currentPath !== pathname) {
            setCurrentPath(pathname)
            setPageViews(pageViews + 1)
        }

        if(pageViews > 1 && pathname !== '/uk') {
            const timer = setTimeout(() => {
                setOpen(true);
            }, 30000);
            return () => clearTimeout(timer);
        }
    }, [lang, pathname]);

    const onOpenChange = (value: boolean) => {
        setOpen(value);
        if (!value) {
            setIsDismissedCookieValue('SignupModal');
        }
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPending(true);
        const data = Object.fromEntries(new FormData(event.currentTarget));
        submitSimpleSignupForm(data)
            .then((response) => {
                setFormState(response);
                event.preventDefault();
                setPending(false);
                setIsDismissedCookieValue('SignupModal');
            })
            .catch((error) => {
                console.error(error);
                setPending(false);
            });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <button style={{ display: 'none' }}>Open Signup</button>
            </DialogTrigger>
            <DialogTitle title="Sign Up" />
            <DialogContent>
                <div className={s.dialogContent}>
                    <div className={cn(s.image, s.desktopOnly)}>
                        <img
                            src="https://cdn.shopify.com/s/files/1/0510/7809/files/sign-up-desktop.jpg?v=1729814136"
                            alt="sign-up"
                        />
                    </div>
                    <div className={cn(s.image, s.mobileOnly)}>
                        <img
                            src="https://cdn.shopify.com/s/files/1/0510/7809/files/sign-up-pop-up-mobile.jpg?v=1729814151"
                            alt="sign-up"
                        />
                        <DialogClose>
                            <div className={cn(s.close, s.mobileOnly)}>
                                <img
                                    className={s.closeIcon}
                                    src={CrossIcon.src}
                                    alt="Cross Icon"
                                />
                            </div>
                        </DialogClose>
                    </div>
                    <div className={s.content}>
                        <DialogClose>
                            <div className={cn(s.close, s.desktopOnly)}>
                                <img
                                    className={s.closeIcon}
                                    src={CrossIcon.src}
                                    alt="Cross Icon"
                                />
                            </div>
                        </DialogClose>
                        <div className={cn('mmds-subtitle-three', s.header)}>
                            Sign up for latest launches, early access and
                            offers, plus 10% off your first order over $150
                        </div>
                        <div className={s.message}>{formState.message}</div>
                        {formState.status !== 200 && (
                            <div className={s.body}>
                                <form onSubmit={onSubmit}>
                                    <div
                                        className={cn(
                                            'mmds-component-two-detail',
                                            s.bodyHeader
                                        )}
                                    >
                                        Riding Styles
                                    </div>
                                    <div className={s.checkbox}>
                                        <Checkbox
                                            label="Road"
                                            id="styleRoad"
                                            name="styleRoad"
                                        />
                                        <Checkbox
                                            label="Gravel"
                                            id="styleGravel"
                                            name="styleGravel"
                                        />
                                        <Checkbox
                                            label="Commute"
                                            id="styleCommute"
                                            name="styleCommute"
                                        />
                                    </div>
                                    <div
                                        className={cn(
                                            'mmds-component-two-detail',
                                            s.bodyHeader
                                        )}
                                    >
                                        Keep me updated about
                                    </div>
                                    <div className={s.checkbox}>
                                        <Checkbox
                                            label="Man"
                                            id="genderMan"
                                            name="genderMan"
                                        />
                                        <Checkbox
                                            label="Woman"
                                            id="genderWoman"
                                            name="genderWoman"
                                        />
                                        <Checkbox
                                            label="All"
                                            id="genderAll"
                                            name="genderAll"
                                        />
                                    </div>
                                    <div>
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            labelText="Email Address"
                                            labelClass="mmds-component-two-detail"
                                            required
                                        />
                                        <input
                                            type="hidden"
                                            name="lang"
                                            value={lang}
                                        />
                                    </div>
                                    <div className={s.button}>
                                        <Button
                                            variant="base"
                                            label={
                                                pending
                                                    ? 'Sending...'
                                                    : 'Submit'
                                            }
                                            disabled={pending}
                                        />
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SignupModal;
